import { useState, useEffect } from 'react';
import { Box, Button, Flex, NumberInput, Select, Stack, Text, TextInput } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';

import { CA_STATES, COUNTRY_CODES, US_STATES } from '../../config';
import { usePrefillConfigForm } from '../../hooks';
import { defaultValues, PREFILL_CONFIG_KEY } from '../../hooks/usePrefillConfigForm';
import type { PrefillConfigFormValues } from '../../hooks/usePrefillConfigForm';

const buildPrefill = (values: PrefillConfigFormValues) => {
  const prefill: Record<string, unknown> = {};
  if (values.firstName) prefill.firstName = values.firstName;
  if (values.lastName) prefill.lastName = values.lastName;
  if (values.email) prefill.email = values.email;
  if (values.phoneNumber && values.phoneRegionCode) {
    prefill.phoneNumber = { number: values.phoneNumber, regionCode: values.phoneRegionCode };
  }
  if (values.country) prefill.country = values.country;
  if (values.stateOrProvince) prefill.stateOrProvince = values.stateOrProvince;
  if (values.monthlyRevenue !== '' && values.monthlyRevenue !== undefined) {
    prefill.monthlyRevenue = values.monthlyRevenue;
  }
  if (values.companyName) prefill.companyName = values.companyName;
  if (values.primaryStoreUrl) prefill.primaryStoreUrl = values.primaryStoreUrl;
  if (values.dateOfIncorporation) {
    const date =
      values.dateOfIncorporation instanceof Date ? values.dateOfIncorporation : new Date(values.dateOfIncorporation);
    prefill.dateOfIncorporation = date.toISOString().split('T')[0];
  }
  return prefill;
};

export const PrefillConfig = () => {
  const form = usePrefillConfigForm();
  const [status, setStatus] = useState<'saved' | 'cleared' | null>(null);

  useEffect(() => {
    if (!status) return;
    const timer = setTimeout(() => setStatus(null), 2000);
    return () => clearTimeout(timer);
  }, [status]);

  const handleSave = () => {
    if (form.validate().hasErrors) return;
    const prefill = buildPrefill(form.values);
    try {
      localStorage.setItem(PREFILL_CONFIG_KEY, JSON.stringify(prefill));
      setStatus('saved');
    } catch (e) {
      console.error('[PrefillConfig] Failed to save config to localStorage:', e);
    }
  };

  const handleClear = () => {
    try {
      localStorage.removeItem(PREFILL_CONFIG_KEY);
    } catch (e) {
      console.error('[PrefillConfig] Failed to remove config from localStorage:', e);
    }
    form.setValues(defaultValues);
    form.resetDirty(defaultValues);
    setStatus('cleared');
  };

  return (
    <>
      <Box px={20} py={16} bg="white" style={{ overflowY: 'auto' }}>
        <Stack gap="sm">
          <TextInput label="First Name" placeholder="Enter first name" {...form.getInputProps('firstName')} />
          <TextInput label="Last Name" placeholder="Enter last name" {...form.getInputProps('lastName')} />
          <TextInput label="Email" placeholder="Enter email" {...form.getInputProps('email')} />
          <TextInput label="Phone Number" placeholder="Enter phone number" {...form.getInputProps('phoneNumber')} />
          <TextInput label="Region Code" placeholder="e.g. US, IE" {...form.getInputProps('phoneRegionCode')} />
          <Select
            label="Country"
            placeholder="Enter Country"
            searchable
            data={COUNTRY_CODES}
            comboboxProps={{
              withinPortal: false,
            }}
            {...form.getInputProps('country')}
            onChange={(event) => form.setValues({ country: event ?? '', stateOrProvince: '' })}
          />

          {form.values.country === 'US' && (
            <Select
              label="State"
              placeholder="Enter State"
              searchable
              data={US_STATES}
              comboboxProps={{
                withinPortal: false,
              }}
              {...form.getInputProps('stateOrProvince')}
            />
          )}

          {form.values.country === 'CA' && (
            <Select
              label="Province"
              placeholder="Enter Province"
              searchable
              data={CA_STATES}
              comboboxProps={{
                withinPortal: false,
              }}
              {...form.getInputProps('stateOrProvince')}
            />
          )}

          <NumberInput
            label="Monthly Revenue"
            allowNegative={false}
            hideControls
            {...form.getInputProps('monthlyRevenue')}
          />
          <TextInput label="Company Name" placeholder="Enter company name" {...form.getInputProps('companyName')} />
          <TextInput label="Primary Store URL" placeholder="https://..." {...form.getInputProps('primaryStoreUrl')} />
          <DatePickerInput
            valueFormat="YYYY-MM-DD"
            label="Date of Incorporation"
            clearable
            popoverProps={{
              withinPortal: false,
            }}
            {...form.getInputProps('dateOfIncorporation')}
          />
        </Stack>
      </Box>
      <Flex direction="column" bg="white" px={16} py={14} gap="sm" mt="auto" style={{ borderRadius: '0 0 12px 12px' }}>
        {status && (
          <Text size="sm" c={status === 'saved' ? 'green' : 'dimmed'} ta="center">
            {status === 'saved' ? 'Prefill config saved' : 'Prefill config cleared'}
          </Text>
        )}
        <Button onClick={handleSave} variant="primary" fullWidth>
          Save
        </Button>
        <Button onClick={handleClear} variant="secondary" fullWidth>
          Clear
        </Button>
      </Flex>
    </>
  );
};
